import {
  RANKING_RULES,
  MATCH_NOTE_MAX,
  RANKING_WEIGHT_LABELS,
  fmtDayTime,
  regionLabel,
  utcOffsetLabel,
} from '@remote-medium/shared';
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  Textarea,
  errorMessage,
} from '@remote-medium/ui';
import { useState } from 'react';
import { Link } from 'react-router';

import { useBillingEnabled } from '../../../lib/billing.tsx';
import { consentLinkExpired, standing, type MatchRow, type Stage } from './stage.ts';

// "Seniority (exact = full, one step = half)" → "Seniority": the row's detail
// line already says why.
const ruleName = (rule: (typeof RANKING_RULES)[number]) =>
  RANKING_WEIGHT_LABELS[rule].replace(/\s*\(.*\)$/, '');

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function hint(m: MatchRow, stage: Stage, company: string, billing: boolean): string {
  switch (stage) {
    case 'review':
      return 'Confirm if this match is worth asking about. Nothing is sent yet.';
    case 'confirmed':
      return 'Nothing is sent until you ask for consent from the Confirmed tab.';
    case 'asked':
      return consentLinkExpired(m)
        ? `Consent request sent ${fmtDayTime(m.consentRequestedAt!)}. The link expired ${fmtDayTime(m.consentExpiresAt!)} without an answer, and they can't be asked again yet.`
        : `Consent request sent ${fmtDayTime(m.consentRequestedAt!)}. Waiting for their answer.`;
    case 'ready':
      return `They said yes. Introduce them from the Ready to introduce tab. ${billing ? `The first introduction on a role uses one of ${company}'s credits.` : 'No credit is used while billing is off.'}`;
    case 'introduced':
      return `Introduced ${fmtDayTime(m.introSentAt!)}. ${company} has their name, email and CV.`;
    case 'rejected':
      return m.candidateConsent === 'no'
        ? 'They declined this role. Nothing more is sent.'
        : 'Not shown to the company. Move it back to review if you change your mind.';
  }
}

/** Notes to save; a field left out keeps the note as it is. */
export interface MatchNotes {
  noteForCandidate?: string | null;
  noteForCompany?: string | null;
}

function NoteField({
  id,
  label,
  placeholder,
  help,
  maxLength,
  value,
  at,
  byName,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  help: string;
  maxLength: number;
  value: string;
  at: string | null;
  byName: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <section className="flex flex-col gap-2">
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <Textarea
        id={id}
        rows={3}
        maxLength={maxLength}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {at && (
        <span className="text-xs text-muted-foreground">
          Last edited {fmtDayTime(at)} by {byName ?? 'a curator'}
        </span>
      )}
      <span className="text-xs text-muted-foreground">{help}</span>
    </section>
  );
}

function ScoreBar({ score, minScore }: { score: number; minScore: number | null }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-semibold tabular-nums">{score} / 100</span>
      <div className="relative h-1.5 flex-1 rounded-full bg-muted" aria-hidden="true">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${Math.min(100, score)}%` }}
        />
        {minScore !== null && (
          <span
            title={`Minimum score ${minScore}`}
            className="absolute -top-1 h-3.5 w-px bg-foreground"
            style={{ left: `${minScore}%` }}
          />
        )}
      </div>
    </div>
  );
}

export function CandidateSheet({
  open,
  m,
  stage,
  company,
  minScore,
  closed,
  onClose,
  onDecide,
  onSaveNotes,
}: {
  /** `m` stays set after closing so the panel can slide out with content. */
  open: boolean;
  m: MatchRow | null;
  stage: Stage | null;
  company: string;
  minScore: number | null;
  closed: boolean;
  onClose: () => void;
  onDecide: (
    m: MatchRow,
    decision: MatchRow['curatorDecision'],
    notes: MatchNotes,
  ) => Promise<void>;
  onSaveNotes: (m: MatchRow, notes: MatchNotes) => Promise<void>;
}) {
  const billing = useBillingEnabled();
  const [forCandidate, setForCandidate] = useState('');
  const [forCompany, setForCompany] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed the drafts when a different match (or a saved note) arrives,
  // during render so there's no frame showing the previous match's notes.
  const seed = JSON.stringify([m?.id, m?.noteForCandidate, m?.noteForCompany]);
  const [seeded, setSeeded] = useState(seed);
  if (seeded !== seed) {
    setSeeded(seed);
    setForCandidate(m?.noteForCandidate ?? '');
    setForCompany(m?.noteForCompany ?? '');
    setError(null);
  }

  // Curator decisions only move before consent is asked; after that the
  // candidate and company drive the match.
  const decidable =
    !closed &&
    (stage === 'review' ||
      stage === 'confirmed' ||
      (stage === 'rejected' && m?.candidateConsent !== 'no'));
  // Notes stay editable at every stage, unlike the decision (Q62).
  const draftCandidate = forCandidate.trim() || null;
  const draftCompany = forCompany.trim() || null;
  const candidateDirty = draftCandidate !== (m?.noteForCandidate ?? null);
  const companyDirty = draftCompany !== (m?.noteForCompany ?? null);
  // Only what changed.
  const changedNotes: MatchNotes = {
    ...(candidateDirty ? { noteForCandidate: draftCandidate } : {}),
    ...(companyDirty ? { noteForCompany: draftCompany } : {}),
  };

  async function act(fn: (m: MatchRow) => Promise<void>) {
    if (!m) return;
    setBusy(true);
    setError(null);
    try {
      await fn(m);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  const decide = (decision: MatchRow['curatorDecision']) =>
    act((m) => onDecide(m, decision, changedNotes));
  const saveNotes = () => act((m) => onSaveNotes(m, changedNotes));

  const seniority = m?.facts?.candidateSeniority;
  // "3 of 4 required skills: Go, React, TypeScript".
  const matched: Partial<Record<(typeof RANKING_RULES)[number], string[] | undefined>> = {
    requiredSkills: m?.facts?.requiredMatched,
    niceToHaveSkills: m?.facts?.niceMatched,
  };

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent>
        {m && stage && (
          <>
            <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-6">
              <header className="flex flex-col gap-1 pr-8">
                <SheetTitle>{m.fullName}</SheetTitle>
                <SheetDescription>
                  {[regionLabel(m.country), utcOffsetLabel(m.timezone), seniority && cap(seniority)]
                    .filter(Boolean)
                    .join(' · ')}
                </SheetDescription>
              </header>

              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold">{standing(m, stage)}</span>
                <span className="text-sm text-muted-foreground">
                  {hint(m, stage, company, billing)}
                </span>
              </div>

              <section className="flex flex-col gap-3">
                <h2 className="m-0 text-xs font-medium text-muted-foreground">
                  Why {m.systemScore}
                </h2>
                <ScoreBar score={m.systemScore} minScore={minScore} />
                <dl className="m-0 flex flex-col">
                  {RANKING_RULES.map((rule) => {
                    const e = m.scoreBreakdown[rule];
                    if (!e?.weight) return null;
                    return (
                      <div
                        key={rule}
                        className="flex items-start justify-between gap-4 border-t border-border py-2.5"
                      >
                        <div className="flex min-w-0 flex-col">
                          <dt className="text-sm">{ruleName(rule)}</dt>
                          <dd className="m-0 text-sm text-muted-foreground">
                            {matched[rule]?.length
                              ? `${e.detail}: ${matched[rule].join(', ')}`
                              : e.detail}
                          </dd>
                        </div>
                        <span className="text-sm font-medium whitespace-nowrap tabular-nums">
                          {e.score} / {e.weight}
                        </span>
                      </div>
                    );
                  })}
                </dl>
              </section>

              <NoteField
                id={`note-candidate-${m.id}`}
                label="Note for the candidate"
                placeholder="Why this role suits them, in a sentence or two."
                help="Shown to the candidate when we ask for their consent. Don't name the company."
                maxLength={MATCH_NOTE_MAX}
                value={forCandidate}
                at={m.noteForCandidateAt}
                byName={m.noteForCandidateByName}
                onChange={setForCandidate}
              />
              <NoteField
                id={`note-company-${m.id}`}
                label={`Note for ${company}`}
                placeholder="Why they fit the role, in a sentence or two."
                help={`Shown to ${company} on the anonymized candidate card once they say yes, and in the introduction email. Don't name the candidate.`}
                maxLength={MATCH_NOTE_MAX}
                value={forCompany}
                at={m.noteForCompanyAt}
                byName={m.noteForCompanyByName}
                onChange={setForCompany}
              />
              <span className="text-xs text-muted-foreground">
                Emails and phone numbers are removed when you save.
              </span>

              <Link
                to={`/candidates/${m.candidateId}`}
                className="w-fit text-sm text-foreground underline underline-offset-4"
              >
                Open full profile
              </Link>
            </div>

            {(decidable || candidateDirty || companyDirty) && (
              <footer className="flex flex-col gap-2 border-t border-border px-6 py-4">
                {error && (
                  <p role="alert" className="m-0 text-sm text-destructive">
                    {error}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {decidable && stage === 'review' && (
                    <>
                      <Button disabled={busy} onClick={() => void decide('confirmed')}>
                        Confirm match
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={busy}
                        onClick={() => void decide('rejected')}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {/* In review, Confirm and Reject save the notes too. */}
                  {!(decidable && stage === 'review') && (candidateDirty || companyDirty) && (
                    <Button disabled={busy} onClick={() => void saveNotes()}>
                      {candidateDirty && companyDirty ? 'Save notes' : 'Save note'}
                    </Button>
                  )}
                  {decidable && stage === 'confirmed' && (
                    <Button variant="ghost" disabled={busy} onClick={() => void decide('rejected')}>
                      Reject
                    </Button>
                  )}
                  {decidable && stage !== 'review' && (
                    <Button variant="ghost" disabled={busy} onClick={() => void decide('pending')}>
                      Move back to review
                    </Button>
                  )}
                </div>
              </footer>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
